<template>
  <div class="erp-phone-root">
    <div v-if="incomingCall" class="erp-phone-toast erp-phone-toast--incoming">
      <div>
        <strong>{{ incomingCall.remoteName || incomingCall.remoteNumber }}</strong>
        <p>Incoming call</p>
      </div>
      <div class="erp-phone-toast__actions">
        <button class="erp-phone-btn erp-phone-btn--success" @click="answerCall">Answer</button>
        <button class="erp-phone-btn erp-phone-btn--danger" @click="rejectCall">Reject</button>
      </div>
    </div>

    <div v-if="dialerVisible" class="erp-phone-panel">
      <div class="erp-phone-panel__header">
        <div>
          <h5>Phone</h5>
          <small>{{ currentStatus }}</small>
        </div>
        <button class="erp-phone-icon-btn" @click="toggleDialer(false)">x</button>
      </div>

      <div v-if="unsupportedError" class="erp-phone-alert erp-phone-alert--danger">
        {{ unsupportedError }}
      </div>
      <div v-else-if="bootstrapError" class="erp-phone-alert erp-phone-alert--danger">
        {{ bootstrapError }}
      </div>

      <div class="erp-phone-panel__body">
        <div class="erp-phone-grid">
          <label>
            Extension
            <select v-model="selectedExtensionId" @change="changeExtension">
              <option v-for="extension in extensions" :key="extension.id" :value="extension.id">
                {{ extension.label || extension.extension }}
              </option>
            </select>
          </label>

          <label>
            Microphone
            <select v-model="selectedInputDeviceId" @change="changeInputDevice">
              <option value="">Default</option>
              <option v-for="device in inputDevices" :key="device.deviceId" :value="device.deviceId">
                {{ device.label || 'Microphone' }}
              </option>
            </select>
          </label>

          <label>
            Speaker
            <select v-model="selectedOutputDeviceId" @change="changeOutputDevice">
              <option value="">Default</option>
              <option v-for="device in outputDevices" :key="device.deviceId" :value="device.deviceId">
                {{ device.label || 'Speaker' }}
              </option>
            </select>
          </label>
        </div>

        <div v-if="permissionError" class="erp-phone-alert erp-phone-alert--warning">
          {{ permissionError }}
        </div>
        <div v-if="registrationError" class="erp-phone-alert erp-phone-alert--danger">
          {{ registrationError }}
        </div>
        <div v-if="disconnectedReason" class="erp-phone-alert erp-phone-alert--warning">
          {{ disconnectedReason }}
        </div>

        <div class="erp-phone-dialer">
          <input
            :value="numberInput"
            class="erp-phone-input"
            placeholder="Enter number"
            @input="updateNumberInput"
          >
          <div class="erp-phone-dialer__actions">
            <button class="erp-phone-btn erp-phone-btn--primary" :disabled="hasActiveCall" @click="placeCall">Call</button>
            <button class="erp-phone-btn" :disabled="registrationState === 'registering'" @click="registerPhone">Reconnect</button>
          </div>
        </div>

        <div v-if="activeCall" class="erp-phone-call-card">
          <div class="erp-phone-call-card__identity">
            <strong>{{ activeCall.remoteName || activeCall.remoteNumber }}</strong>
            <span>{{ activeCall.statusLabel || activeCall.status }}</span>
            <small v-if="activeCall.answeredAt">{{ formatDuration(activeCall.durationSeconds) }}</small>
          </div>
          <div class="erp-phone-call-card__controls">
            <button class="erp-phone-btn" @click="toggleMute">{{ isMuted ? 'Unmute' : 'Mute' }}</button>
            <button class="erp-phone-btn" @click="toggleHold">{{ isOnHold ? 'Resume' : 'Hold' }}</button>
            <button class="erp-phone-btn" @click="toggleSpeaker">{{ speakerEnabled ? 'Speaker On' : 'Speaker Off' }}</button>
            <button class="erp-phone-btn erp-phone-btn--danger" @click="endCall">End</button>
          </div>
          <div class="erp-phone-keypad">
            <button v-for="digit in digits" :key="digit" @click="pressDigit(digit)">{{ digit }}</button>
          </div>
        </div>

        <div v-if="!activeCall && !incomingCall" class="erp-phone-history">
          <div class="erp-phone-history__header">
            <strong>Recent calls</strong>
            <button class="erp-phone-link-btn" @click="refreshLogs">Refresh</button>
          </div>
          <div v-if="callLogsLoading" class="erp-phone-empty">Loading call history...</div>
          <div v-else-if="callLogsError" class="erp-phone-empty">{{ callLogsError }}</div>
          <div v-else-if="!callLogs.length" class="erp-phone-empty">No call history yet.</div>
          <div v-else class="erp-phone-history__list">
            <div v-for="log in callLogs" :key="log.id" class="erp-phone-history__item">
              <div>
                <strong>{{ log.remote_name || log.remote_number }}</strong>
                <small>{{ log.direction }} • {{ log.status }}</small>
              </div>
              <div class="text-right">
                <small>{{ formatDate(log.started_at) }}</small>
                <small>{{ formatDuration(log.duration_seconds || 0) }}</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { phoneService } from '@/services/phoneService'

export default {
  name: 'PhoneRoot',
  data () {
    return {
      digits: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'],
      logoutListener: null
    }
  },
  computed: {
    phoneState () {
      return this.$store.state.phone
    },
    extensions () {
      return this.phoneState.extensions
    },
    selectedExtensionId: {
      get () {
        return this.phoneState.selectedExtensionId
      },
      set (value) {
        this.$store.commit('phone/SET_SELECTED_EXTENSION_ID', Number(value))
      }
    },
    selectedInputDeviceId: {
      get () {
        return this.phoneState.selectedInputDeviceId
      },
      set (value) {
        this.$store.commit('phone/SET_SELECTED_INPUT_DEVICE_ID', value)
      }
    },
    selectedOutputDeviceId: {
      get () {
        return this.phoneState.selectedOutputDeviceId
      },
      set (value) {
        this.$store.commit('phone/SET_SELECTED_OUTPUT_DEVICE_ID', value)
      }
    },
    inputDevices () {
      return this.phoneState.availableInputDevices
    },
    outputDevices () {
      return this.phoneState.availableOutputDevices
    },
    callLogs () {
      return this.phoneState.callLogs
    },
    callLogsLoading () {
      return this.phoneState.callLogsLoading
    },
    callLogsError () {
      return this.phoneState.callLogsError
    },
    numberInput () {
      return this.phoneState.numberInput
    },
    registrationState () {
      return this.phoneState.registrationState
    },
    registrationError () {
      return this.phoneState.registrationError
    },
    bootstrapError () {
      return this.phoneState.bootstrapError
    },
    disconnectedReason () {
      return this.phoneState.disconnectedReason
    },
    unsupportedError () {
      return this.phoneState.unsupportedError
    },
    permissionError () {
      return this.phoneState.permissionError
    },
    activeCall () {
      return this.phoneState.activeCall
    },
    incomingCall () {
      return this.phoneState.incomingCall
    },
    hasActiveCall () {
      return !!this.activeCall
    },
    isMuted () {
      return this.phoneState.isMuted
    },
    isOnHold () {
      return this.phoneState.isOnHold
    },
    speakerEnabled () {
      return this.phoneState.speakerEnabled
    },
    dialerVisible () {
      return this.phoneState.dialerVisible
    },
    currentStatus () {
      return this.$store.getters['phone/currentStatusText']
    }
  },
  watch: {
    dialerVisible (value) {
      if (value) {
        this.refreshLogs()
      }
      this.$store.dispatch('phone/persistPreferences')
    }
  },
  async created () {
    if (localStorage.getItem('accessToken')) {
      await phoneService.bootstrap()
    }
    this.logoutListener = () => {
      phoneService.dispose({ keepState: false })
    }
    window.addEventListener('phone:logout', this.logoutListener)
  },
  beforeDestroy () {
    if (this.logoutListener) {
      window.removeEventListener('phone:logout', this.logoutListener)
    }
  },
  methods: {
    async changeExtension () {
      await phoneService.setSelectedExtension(this.selectedExtensionId)
    },
    async changeInputDevice () {
      await phoneService.setInputDevice(this.selectedInputDeviceId)
    },
    async changeOutputDevice () {
      await phoneService.setOutputDevice(this.selectedOutputDeviceId)
    },
    updateNumberInput (event) {
      this.$store.commit('phone/SET_NUMBER_INPUT', event.target.value)
    },
    async placeCall () {
      try {
        await phoneService.dial(this.numberInput)
      } catch (error) {
        this.$store.commit('phone/SET_REGISTRATION_ERROR', error.message || 'Unable to place call.')
      }
    },
    async registerPhone () {
      try {
        await phoneService.registerSelectedExtension()
      } catch (error) {
        this.$store.commit('phone/SET_REGISTRATION_ERROR', error.message || 'Unable to reconnect phone.')
      }
    },
    async answerCall () {
      await phoneService.answer()
    },
    async rejectCall () {
      await phoneService.reject()
    },
    async endCall () {
      await phoneService.end()
    },
    async toggleMute () {
      await phoneService.toggleMute()
    },
    async toggleHold () {
      await phoneService.toggleHold()
    },
    async toggleSpeaker () {
      this.$store.commit('phone/SET_SPEAKER_ENABLED', !this.speakerEnabled)
      await this.$store.dispatch('phone/persistPreferences')
      await phoneService.applyOutputDevice()
    },
    pressDigit (digit) {
      phoneService.sendDtmf(digit)
      this.$store.commit('phone/SET_NUMBER_INPUT', `${this.numberInput}${digit}`)
    },
    toggleDialer (forceValue = null) {
      this.$store.commit('phone/SET_DIALER_VISIBLE', forceValue)
    },
    async refreshLogs () {
      await phoneService.fetchCallLogs()
    },
    formatDuration (seconds) {
      const total = Number(seconds || 0)
      const mins = Math.floor(total / 60)
      const secs = total % 60
      return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
    },
    formatDate (value) {
      if (!value) return ''
      return new Date(value).toLocaleString()
    }
  }
}
</script>
